import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { Router } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ActiveFilterContentComponent } from '@connect-services/client/components/active-filter-content/active-filter-content.component';
import { FilterMenuComponent } from '@connect-services/client/components/filter-menu/filter-menu.component';
import { GlobalSearchComponent } from '@connect-services/client/components/global-search/global-search.component';
import { LayerSelectComponent } from '@connect-services/client/components/layer-select/layer-select.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ContentGridOptions, ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { FieldInterventionChangeSelectionService } from '@connect-services/client/services/field-intervention-change-selection.service';
import { FilterTypeEnum } from '@connect-services/client/services/filter.service';
import { FilterResolverService } from '@connect-services/client/services/filter-resolver.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { RouterService } from '@connect-services/client/services/router.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { PermissionAliasEnum } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ActiveFilterContentComponent,
        ButtonComponent,
        FilterMenuComponent,
        GlobalSearchComponent,
        LayerSelectComponent,
        RessourcesModule,
        SvgIconComponent,
    ],
    selector: 'app-content-toolbar',
    styleUrl: './content-toolbar.component.scss',
    templateUrl: './content-toolbar.component.html',
})
export class ContentToolbarComponent {
    public readonly allowMapSearch!: Signal<boolean>;
    public readonly currentUrl!: Signal<string[] | undefined>;

    protected readonly ButtonType = ButtonType;
    protected readonly contentLayoutService = inject(ContentLayoutService);
    protected readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    protected readonly filterResolverService = inject(FilterResolverService);
    protected readonly FilterTypeEnum = FilterTypeEnum;
    protected readonly hasPermissionsService = inject(HasPermissionsService);
    protected readonly hasTableViewPermissions = computed(() => {
        return (
            this.isTableViewEnabled() && this.hasPermissionsService.checkPermissions([PermissionAliasEnum.READ_LAYER])
        );
    });
    protected readonly isFieldInterventionSelectionToolbarVisible = computed(() => {
        return (
            this.createFieldInterventionService.isCreateFieldIntervention() ||
            this.fieldInterventionChangeSelectionService.isActive()
        );
    });
    protected readonly isTableEditModeActive = computed(() => {
        return this.contentLayoutService.isDataTableView() && this.projectEditModeService.isEditModeActive();
    });
    protected readonly isTableViewEnabled = computed(() => {
        return ![
            RoutePathEnum.FIELD_INTERVENTION_CHANGE_SELECTION,
            RoutePathEnum.FIELD_INTERVENTION_CREATE,
            RoutePathEnum.FIELD_INTERVENTION_DASHBOARD,
            RoutePathEnum.FIELD_INTERVENTION_ENTITIES,
            RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID,
            RoutePathEnum.FIELD_INTERVENTION_FORMS,
            RoutePathEnum.FIELD_INTERVENTION_FORM_ID,
            RoutePathEnum.FIELD_INTERVENTION_ID,
            RoutePathEnum.FIELD_INTERVENTION_LAYERS,
            RoutePathEnum.FIELD_INTERVENTION_LAYER_ID,
            RoutePathEnum.FIELD_INTERVENTION_PLANNING,
        ].includes(this.contentLayoutService.currentRoutePath());
    });
    protected readonly mapFilterService: MapFilterService = inject(MapFilterService);
    protected readonly Object = Object;
    protected readonly panelService = inject(PanelService);
    protected readonly PermissionAliasEnum = PermissionAliasEnum;

    private readonly broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.VIEW);
    private readonly fieldInterventionChangeSelectionService = inject(FieldInterventionChangeSelectionService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly router = inject(Router);
    private readonly routerService = inject(RouterService);

    public constructor() {
        this.broadcastChannel.postMessage(<BroadcastChannelEvent<{ isTableView: boolean }>>{
            message: BroadcastChannelMessageEnum.ASK_VIEW,
            source: BroadcastChannelSourceEnum.ENTITY_DETAIL,
        });

        this.broadcastChannel.onmessage = (
            event: MessageEvent<BroadcastChannelEvent<{ isTableView: boolean }>>,
        ): void => {
            if (
                event.data.source !== BroadcastChannelSourceEnum.ENTITY_DETAIL ||
                event.data.message !== BroadcastChannelMessageEnum.ASK_VIEW
            ) {
                return;
            }

            this.broadcastChannel.postMessage(<BroadcastChannelEvent<{ isTableView: boolean }>>{
                additionalData: { isTableView: this.contentLayoutService.isDataTableView() },
                message: BroadcastChannelMessageEnum.CHANGE_VIEW,
                source: BroadcastChannelSourceEnum.HOMEPAGE,
            });
        };

        this.currentUrl = this.routerService.getUrl();

        this.allowMapSearch = computed(() => {
            return (
                !!this.currentUrl() &&
                !this.currentUrl()!.some((token: string) => {
                    return [
                        RoutePathEnum.FIELD_INTERVENTIONS,
                        RoutePathEnum.MANAGEMENT_DATA,
                        RoutePathEnum.MANAGEMENT_ORGANIZATION,
                    ].includes(token as RoutePathEnum);
                })
            );
        });
    }

    public async goBack(): Promise<void> {
        await this.router.navigate([
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()!.id,
            RoutePathEnum.FIELD_INTERVENTIONS,
        ]);
    }

    public async toggleMapSizeType(event: MatButtonToggleChange): Promise<void> {
        const selectedLayout: ContentGridOptions = event.value;

        if (!selectedLayout) {
            return;
        }

        this.contentLayoutService.setActiveLayoutKey(selectedLayout.class);

        this.contentLayoutService.mapSizeType.set(selectedLayout);
        this.contentLayoutService.initialMapSizeType.set(null);

        const fullScreenLayout = this.contentLayoutService.gridParams().FULL_SCREEN;

        if (!fullScreenLayout) {
            return;
        }

        if (selectedLayout.class !== fullScreenLayout.class) {
            return;
        }

        await this.router.navigate([RoutePathEnum.PROJECTS, this.projectBloc.value()!.id]);
    }
}
